import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling, ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { Database } from '@/integrations/supabase/types';

type ShortLink = Database['public']['Tables']['short_links']['Row'];
type ShortLinkInsert = Database['public']['Tables']['short_links']['Insert'];
type LinkClick = Database['public']['Tables']['link_clicks']['Row'];

export class LinksService {
  private static readonly TABLE = 'short_links';
  private static readonly logger = logger.child({ service: 'LinksService' });

  /**
   * Get all short links for an organization with pagination
   */
  static async getAll(organizationId: string, options?: { limit?: number; offset?: number }) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching short links', { organizationId, options });

      const { data, error, count } = await supabase
        .from('short_links')
        .select('*', { count: 'exact' })
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .range(options?.offset || 0, (options?.offset || 0) + (options?.limit || 50) - 1);

      if (error) throw error;

      this.logger.info('Short links fetched', { 
        organizationId, 
        count: data?.length || 0 
      });

      return { data: data || [], count: count || 0 };
    }, { operation: 'getAll', table: this.TABLE });
  }

  /**
   * Generate default UTM parameters for tracking
   */
  private static generateDefaultUTMs(
    postId?: string,
    campaignId?: string
  ): Record<string, string> {
    const utms: Record<string, string> = {
      utm_source: 'growth_social',
      utm_medium: 'social',
    };

    if (campaignId) {
      utms.utm_campaign = campaignId;
    }

    if (postId) {
      utms.utm_content = postId;
    }

    return utms;
  }

  /**
   * Get a single short link by ID
   */
  static async getById(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching short link', { linkId: id });

      const { data, error } = await supabase
        .from('short_links')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Short link fetched', { linkId: id, found: !!data });

      return data;
    }, { operation: 'getById', table: this.TABLE });
  }

  /**
   * Create a short link with auto-UTM generation
   */
  static async create(link: ShortLinkInsert & { postId?: string; campaignId?: string }) {
    return withServiceErrorHandling(async () => {
      // Auto-generate UTM parameters if not already in URL
      let finalUrl = link.original_url;
      if (!finalUrl.includes('utm_source')) {
        const utmParams = this.generateDefaultUTMs(link.postId, link.campaignId);
        const url = new URL(finalUrl);
        Object.entries(utmParams).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
        finalUrl = url.toString();
      }

      this.logger.info('Creating short link', { 
        organizationId: link.organization_id,
        shortCode: link.short_code 
      });

      const { data, error } = await supabase
        .from('short_links')
        .insert({ ...link, original_url: finalUrl })
        .select()
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Short link created', { linkId: data?.id });

      return data;
    }, { operation: 'create', table: this.TABLE });
  }

  /**
   * Delete a short link (soft delete)
   */
  static async delete(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Soft deleting short link', { linkId: id });

      const { error } = await supabase
        .from(this.TABLE)
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      this.logger.info('Short link soft deleted successfully', { linkId: id });
      return true;
    }, { operation: 'delete', table: this.TABLE });
  }

  /**
   * Permanently delete a short link (admin only)
   */
  static async hardDelete(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.warn('HARD deleting short link', { linkId: id });

      const { error } = await supabase
        .from(this.TABLE)
        .delete()
        .eq('id', id);

      if (error) throw error;

      this.logger.warn('Short link permanently deleted', { linkId: id });
      return true;
    }, { operation: 'hardDelete', table: this.TABLE });
  }

  /**
   * Create tracking link for a campaign post
   */
  static async createCampaignTrackingLink(
    campaignId: string,
    postSequence: number,
    organizationId: string,
    destinationURL: string,
    campaignSlug: string
  ) {
    const shortCode = `${campaignSlug}-${postSequence}`;
    
    return this.create({
      organization_id: organizationId,
      original_url: destinationURL,
      short_code: shortCode
    });
  }

  /**
   * Create tracking link for a single post
   */
  static async createPostTrackingLink(
    postId: string,
    organizationId: string,
    destinationURL: string
  ) {
    const shortCode = `post-${postId.substring(0, 8)}`;
    
    return this.create({
      organization_id: organizationId,
      original_url: destinationURL,
      short_code: shortCode,
      post_id: postId
    });
  }

  /**
   * Get analytics for a link
   */
  static async getAnalytics(linkId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching link analytics', { linkId });

      const { data, error } = await supabase
        .from('link_clicks')
        .select('*')
        .eq('short_link_id', linkId);

      if (error) throw error;

      const clicks = data || [];
      
      const stats = {
        totalClicks: clicks.length,
        browsers: {} as Record<string, number>,
        devices: {} as Record<string, number>,
        countries: {} as Record<string, number>,
      };

      clicks.forEach(click => {
        if (click.browser) {
          stats.browsers[click.browser] = (stats.browsers[click.browser] || 0) + 1;
        }
        if (click.device_type) {
          stats.devices[click.device_type] = (stats.devices[click.device_type] || 0) + 1;
        }
        if (click.country) {
          stats.countries[click.country] = (stats.countries[click.country] || 0) + 1;
        }
      });

      this.logger.info('Link analytics calculated', { 
        linkId, 
        totalClicks: stats.totalClicks 
      });

      return stats;
    }, { operation: 'getAnalytics', table: 'link_clicks' });
  }
}
